'use client';

import type { ReactNode } from 'react';
import type { Breakpoint } from '@mui/material/styles';

import { merge } from 'es-toolkit';
import { usePathname } from 'next/navigation';
import { useBoolean } from 'minimal-shared/hooks';
import { useRef, useState, useCallback, useLayoutEffect } from 'react';

import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { iconButtonClasses } from '@mui/material/IconButton';

// import { _contacts, _notifications } from 'src/_mock';
import { useUser } from 'src/hooks/use-user';

import { getCurrentPlan } from 'src/utils/util-user';

import { RADIUS } from 'src/theme/ui-tokens';
import { AnalyticsProvider } from 'src/lib/analytics/react'
import { IWorkspacePlan } from 'src/constants/plan-service';
import { NavUpgrade } from 'src/layouts/components/nav-upgrade';
import { DEMO_BANNER_MAP, DEMO_BANNER_DEFAULT_MESSAGE } from 'src/constants/demo-banner';

// import { _workspaces } from '../nav-config-workspace';
import { Logo } from 'src/components/logo';
import { useSettingsContext } from 'src/components/settings';
import { navSectionClasses } from 'src/components/nav-section';
import { PageHeadingProvider } from 'src/components/cmui/page-heading/page-heading-context';

import { useMenuJobs } from 'src/sections/common/hooks/load/use-menu-jobs';
import { useMenuSets } from 'src/sections/common/hooks/load/use-menu-sets';
import { useDemoActionGuard } from 'src/sections/common/demo/use-demo-guard';
import { useResearchUsage } from 'src/sections/common/hooks/load/use-research-usage';
import { ServicePopupHost } from 'src/sections/common/service-popup/service-popup-host';
import { useOnboardingBadge } from 'src/sections/common/hooks/model/use-onboarding-checklist';

import { NavMobile } from './nav-mobile';
import { VerticalDivider } from './content';
import { NavVertical } from './nav-vertical';
import { layoutClasses } from '../core/classes';
import { NavHorizontal } from './nav-horizontal';
import { resolveNavHeading } from '../nav-heading';
import { MainSection } from '../core/main-section';
import { MenuButton } from '../components/menu-button';
import { HeaderSection } from '../core/header-section';
import { LayoutSection } from '../core/layout-section';
import { AccountDrawer } from '../components/account-drawer';
import { ACCOUNT_HEADING_EXTRAS } from '../nav-config-account';
import { getNavData, getFullNavData } from '../nav-config-service';
import { WorkspacesPopover } from '../components/workspaces-popover';
import { dashboardLayoutVars, dashboardNavColorVars } from './css-vars';

import type { MainSectionProps } from '../core/main-section';
import type { LayoutSectionProps } from '../core/layout-section';
import type { HeaderSectionProps } from '../core/header-section';

// ----------------------------------------------------------------------

/**
 * `usePathname()` 값을 맵 매칭에 쓰기 좋게 정규화합니다 (끝의 `/` 제거, `/` 루트는 유지).
 * @param {string | null | undefined} pathname
 * @returns {string}
 */
function normalizePathnameForDemoBanner(pathname: string | null | undefined) {
  if (pathname == null) return '';
  let path = String(pathname).trim();
  if (path === '') return '';
  if (path !== '/' && path.endsWith('/')) {
    path = path.replace(/\/+$/, '') || '/';
  }
  return path;
}

/**
 * 현재 pathname과 `DEMO_BANNER_MAP`을 매칭해 데모 배너 문구를 반환합니다.
 * - 첫 번째로 일치하는 항목의 `message`를 사용합니다.
 * - **어느 항목과도 맞지 않으면** `DEMO_BANNER_DEFAULT_MESSAGE`를 반환합니다 (미정의·기타 경로 대비).
 *
 * @param {string} pathname - `usePathname()` 등, 예: '/service', '/service/discover/bookmarks/abc'
 * @returns {string}
 */
function getDemoBannerMessageForPath(pathname: string | null | undefined) {
  const path = normalizePathnameForDemoBanner(pathname);

  for (const item of DEMO_BANNER_MAP) {
    const { path: pattern, message } = item;
    if (pattern instanceof RegExp) {
      if (pattern.test(path)) {
        return message;
      }
    } else if (typeof pattern === 'string' && pattern) {
      if (path === pattern || path.startsWith(`${pattern}/`)) {
        return message;
      }
    }
  }

  return DEMO_BANNER_DEFAULT_MESSAGE;
}

/** 데모 배너 마퀴: 한 절(문구+간격)이 지나가는 데 걸리는 시간(초). 절 개수와 무관하게 동일한 체감 속도 */

/** 숨김 전 전체 메뉴 — 사이드바에서 빠진 화면을 주소로 열어도 머리 이름이 서게 하는 폴백 */
const FULL_NAV_DATA = getFullNavData() as unknown[];

const DEMO_BANNER_MARQUEE_DURATION_SEC = 10;

/** 마퀴 무한 루프 최소 절 수(항상 이 값 이상이면 끊김 없이 흐름) */
const DEMO_BANNER_MARQUEE_MIN_LOOP_COPIES = 2;
const DEMO_BANNER_MARQUEE_MAX_COPIES = 10;

const demoBannerMarqueeSegmentSx = {
  fontWeight: 600,
  lineHeight: 1.25,
  whiteSpace: 'nowrap',
  flexShrink: 0,
  pr: 10,
};

const demoBannerMarqueeMaskSx = {
  WebkitMaskImage:
    'linear-gradient(to right, transparent, #000 10%, #000 90%, transparent)',
  maskImage:
    'linear-gradient(to right, transparent, #000 10%, #000 90%, transparent)',
  WebkitMaskSize: '100% 100%',
  maskSize: '100% 100%',
  WebkitMaskRepeat: 'no-repeat',
  maskRepeat: 'no-repeat',
};

/**
 * 뷰포트 대비 한 절 너비로 마퀴 절 개수를 정합니다.
 * `ceil(뷰/절)+1`을 [MIN, MAX]로 클램프합니다. 한 절이 뷰보다 길어도 최소 2절로 마퀴합니다.
 *
 * @param {number} containerWidthPx
 * @param {number} segmentWidthPx - 문구 + 오른쪽 간격(px)
 * @returns {number}
 */
function computeDemoMarqueeCopyCount(containerWidthPx: number, segmentWidthPx: number) {
  if (
    !Number.isFinite(containerWidthPx) ||
    !Number.isFinite(segmentWidthPx) ||
    segmentWidthPx <= 0
  ) {
    return DEMO_BANNER_MARQUEE_MIN_LOOP_COPIES;
  }

  const slotsToCover = Math.ceil(containerWidthPx / segmentWidthPx);
  const withLoopPad = slotsToCover + 1;

  return Math.min(
    DEMO_BANNER_MARQUEE_MAX_COPIES,
    Math.max(DEMO_BANNER_MARQUEE_MIN_LOOP_COPIES, withLoopPad)
  );
}

function DemoBannerMarquee({ message, durationSec }: { message: string; durationSec: number }) {
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const containerRef = useRef<HTMLDivElement | null>(null);
  const segmentMeasureRef = useRef<HTMLSpanElement | null>(null);
  const [copyCount, setCopyCount] = useState(DEMO_BANNER_MARQUEE_MIN_LOOP_COPIES);

  const recalcCopyCount = useCallback(() => {
    const containerEl = containerRef.current;
    const segmentEl = segmentMeasureRef.current;
    if (!containerEl || !segmentEl) return;

    const cw = containerEl.getBoundingClientRect().width;
    const sw = segmentEl.getBoundingClientRect().width;

    setCopyCount(computeDemoMarqueeCopyCount(cw, sw));
  }, []);

  useLayoutEffect(() => {
    recalcCopyCount();
  }, [message, isSmUp, recalcCopyCount]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const ro = new ResizeObserver(() => {
      recalcCopyCount();
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, [recalcCopyCount]);

  const containerShellSx = {
    position: 'relative',
    overflow: 'hidden',
    width: '100%',
    minHeight: '1.25rem',
    display: 'flex',
    alignItems: 'center',
    ...demoBannerMarqueeMaskSx,
  };

  const shiftPercent = 100 / copyCount;
  const animationName = `demoBannerMarquee_c${copyCount}`;

  return (
    <Box ref={containerRef} sx={containerShellSx}>
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          visibility: 'hidden',
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        <Typography
          ref={segmentMeasureRef}
          component="span"
          variant="body2"
          sx={demoBannerMarqueeSegmentSx}
        >
          {message}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          width: 'max-content',
          flexShrink: 0,
          animation: `${animationName} ${durationSec}s linear infinite`,
          willChange: 'transform',
          [`@keyframes ${animationName}`]: {
            '0%': { transform: 'translate3d(0, 0, 0)' },
            '100%': { transform: `translate3d(-${shiftPercent}%, 0, 0)` },
          },
        }}
      >
        {Array.from({ length: copyCount }, (_, copyIndex) => (
          <Typography
            key={copyIndex}
            component="span"
            variant="body2"
            sx={demoBannerMarqueeSegmentSx}
            aria-hidden={copyIndex !== 0}
          >
            {message}
          </Typography>
        ))}
      </Box>

    </Box>
  );
}

// ----------------------------------------------------------------------

type ServiceLayoutProps = LayoutSectionProps & {
  layoutQuery?: Breakpoint;
  /** 진입 로그(`AnalyticsProvider`)를 거는가 — 미리보기처럼 로그를 남기지 않을 판만 끈다(기본 참) */
  analytics?: boolean;
  slotProps?: {
    header?: HeaderSectionProps;
    nav?: { data?: unknown };
    main?: MainSectionProps;
  };
};

/**
 * 틀(`ServiceLayoutFrame`)에 끼우는 **사람에 따라 다른 자리** — 로그인한 판(`ServiceLayout`)과 비회원 판(`GuestLayout`)이 따로 채운다.
 * 🔑 틀은 하나다 — 메뉴·머리·발치의 모양과 자리는 두 판이 같고, 안에 든 것만 다르다.
 */
export type ServiceLayoutSlots = {
  /**
   * 워크스페이스 고르개 자리 — 메뉴 머리(`nav`)와 좁은 화면 머리띠(`header`)가 부른다.
   * @param horizontal 가로 메뉴 모드인가(머리띠 글자색)
   */
  workspace: (where: 'nav' | 'header', horizontal: boolean) => ReactNode;
  /** 머리띠 오른쪽 — 계정 */
  account: ReactNode;
  /** 왼쪽 메뉴 발치 */
  navFooter: ReactNode;
};

type ServiceLayoutFrameProps = ServiceLayoutProps & {
  /** 사이드바 목록(`getNavData` · 비회원은 `getGuestNavData`) */
  navData: unknown;
  slots: ServiceLayoutSlots;
  /** 데모 띠 문구 — `null` 이면 띠를 안 세운다 */
  demoBannerMessage: string | null;
  /** 참이면 저장된 가로 메뉴 설정을 무시하고 세로 메뉴로 연다 */
  forceVertical?: boolean;
  /** 보는 사람의 역할 — 역할 조건이 붙은 줄을 가른다(비회원은 없음) */
  role?: string;
};

/**
 * **로그인한 사람의 서비스 틀** — 사용자·워크스페이스에서 메뉴·띠·고르개를 짓고 틀(`ServiceLayoutFrame`)에 넘긴다.
 * ⚠️ 여기의 구독(`useMenuSets`·`useMenuJobs`·결제 이력)은 로그인한 판에서만 돈다 — 비회원 판은 이 함수를 거치지 않는다.
 */
export function ServiceLayout(props: ServiceLayoutProps) {
  const { slotProps } = props;
  const pathname = usePathname();

  const user = useUser();
  const { isDemo } = useDemoActionGuard();
  const currentPlan = getCurrentPlan(user)

  /*
    🔑 **줄 목록은 구독으로 받는다** — 화면을 저장하는 순간 사이드바에 그 줄이 선다.
      `user` 가 실어 온 것만 쓰면 새로고침해야 보인다.
    ⚠️ 목록이 아직 안 왔으면(`ready` 전) 넘기지 않는다 — 기본 셋으로 잠깐 깜빡이지 않게.
  */
  const { sets: liveMenuSets, campaigns: liveCampaigns, campaignsReady, workspace: liveWorkspace, ready: menuSetsReady } = useMenuSets();
  /* 담아 둔 작업 — 고정한 것만 TASK SUPPORT 로 뽑힌다(`utils/workspace/menu-jobs`) */
  const { jobs: liveMenuJobs, hiddenTasks: liveHiddenTasks, ready: menuJobsReady } = useMenuJobs();
  /* 초기 설정 진행 「3/4」 — 「추적 관리」 옆 배지(구독 없이 세션 사본으로 센다) */
  const onboardingBadge = useOnboardingBadge();
  /* 이번 달 Research 「0/30」 — RESEARCH 표제 줄 오른쪽 끝(한도가 있는 플랜만) */
  const researchUsage = useResearchUsage();
  const navData = slotProps?.nav?.data
    ?? getNavData(
      user,
      isDemo,
      menuSetsReady ? liveMenuSets : null,
      menuJobsReady ? liveMenuJobs : null,
      menuJobsReady ? liveHiddenTasks : null,
      /* 🚨 갈래 판정은 **살아 있는 세트 문서**로 한다 — 세션의 `campaignList` 는 계정이 빠진 파생본이다 */
      menuSetsReady ? liveCampaigns : null,
      onboardingBadge,
      researchUsage ? `${researchUsage.used}/${researchUsage.limit}` : null,
    );

  /*
    🔑 **넓은 화면에서는 머리띠가 없다** — 워크스페이스·계정이 왼쪽 메뉴 머리로 올라갔다.
      그래서 띠 판정을 여기서 한 번 하고 머리띠와 왼쪽 메뉴가 **같은 값**을 본다.
    ⚠️ 데모 띠는 경로마다 말이 다르다 — 좁은 화면에서만 보이면 데모 고객이 안내를 놓친다.
  */
  const showDemoBanner = currentPlan?.planId === IWorkspacePlan.planId.plan_demo;
  const demoBannerMessage = showDemoBanner ? getDemoBannerMessageForPath(pathname) : null;

  const slots: ServiceLayoutSlots = {
    workspace: (_where, horizontal) => (
      <WorkspacesPopover
        data={user?.workspaces || []}
        user={user}
        sx={{ ...(horizontal && { color: 'var(--layout-nav-text-primary-color)' }) }}
      />
    ),
    account: <AccountDrawer />,
    /* 🔑 워크스페이스·계정은 **카드 하나**가 쥔다(`NavUpgrade`) — 발치에 또 세우지 않는다 */
    navFooter: <NavUpgrade />,
  };

  return (
    <>
      <ServiceLayoutFrame
        {...props}
        navData={navData}
        slots={slots}
        demoBannerMessage={demoBannerMessage}
        role={user?.role as string | undefined}
      />
      {/* 체험 끝나 감 · 초기 설정 안 함 · 계정 연동 끊김 — 서비스에 머문 지 30초 뒤 한 장(`/service` 아래에서만) */}
      <ServicePopupHost live={{ workspace: liveWorkspace, campaigns: liveCampaigns, campaignsReady }} pathname={pathname} />
    </>
  );
}

/**
 * **서비스 틀** — 왼쪽 메뉴(머리·목록·발치) · 좁은 화면 머리띠 · 본문. 사람에 따라 다른 것은 받기만 한다(`ServiceLayoutSlots` · 띠 둘 · 목록).
 * ⚠️ 여기서 사용자·워크스페이스를 읽지 않는다 — 비회원 판(질문 대화 `/ask`)도 이 틀을 쓴다.
 */
export function ServiceLayoutFrame({
  sx,
  cssVars,
  children,
  slotProps,
  layoutQuery = 'lg',
  analytics = true,
  navData,
  slots,
  demoBannerMessage,
  forceVertical = false,
  role,
}: ServiceLayoutFrameProps) {
  const theme = useTheme();
  const pathname = usePathname();
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  const settings: {
    state: { navColor?: string; navLayout?: string };
    setField: (name: string, value: unknown) => void;
  } = useSettingsContext();

  const navLayout = forceVertical ? 'vertical' : settings.state.navLayout;
  const navVars = dashboardNavColorVars(theme, settings.state.navColor, navLayout);

  const { value: open, onFalse: onClose, onTrue: onOpen } = useBoolean();

  /*
    🚨 **접기(mini)는 닫아 두었다** — 88px 폭에서 발치 카드(`NavUpgrade`)의 세로 배치가 서로 겹치고,
      머리(로고·워크스페이스)도 그 폭에 못 들어간다. 저장된 설정이 `mini` 여도 펼친 채로 연다.
    🔑 배선은 살아 있다(`renderNavMini`·`NavSectionMini`) — 이 값을 설정에서 다시 읽게 하면 돌아온다.
  */
  const isNavMini = false;
  const isNavHorizontal = navLayout === 'horizontal';
  const isNavVertical = isNavMini || navLayout === 'vertical';

  /* 이름과 달리 「가릴 것인가」 — 역할 조건이 붙은 줄은 그 역할일 때만 선다(비회원은 역할이 없어 가린다) */
  const canDisplayItemByRole = (allowedRoles?: string[]) =>
    !allowedRoles?.includes(role as string);


  /*
    🔑 **왼쪽 메뉴의 머리·발치는 한 벌이다** — 넓은 화면의 고정 메뉴(`NavVertical`)와 좁은 화면의
      서랍(`NavMobile`)이 **같은 것**을 쓴다. 갈라 두면 창을 줄였을 때 다른 제품처럼 보인다.
  */
  const navSlots = {
    topArea: (
      <Box sx={{ px: 2, pt: 2.5, pb: 1 }}>
        {/* 🔑 「Assemble」 모션 큐브는 넓은 화면의 고정 메뉴에만 붙는다 — 좁은 화면의 서랍은 워드마크만 */}
        <Box sx={{ pl: 0.5, mb: 1 }}>
          <Logo
            isSingle={false}
            iconSx={{ display: 'none', [theme.breakpoints.up(layoutQuery)]: { display: 'block' } }}
          />
        </Box>
        {/*
          🔑 **워크스페이스 고르개는 머리에 둔다** — 이걸 바꾸면 화면의 모든 값이 통째로 바뀌는
            가장 위 조건이라, 「지금 어느 자리에서 보는가」가 한눈에 보여야 한다.
          ⚠️ 로고와 같은 줄에 두지 않는다 — 칸이 좁아 이름이 「캐치모…」로 잘린다.
        */}
        <Box sx={{ minWidth: 0, display: 'flex' }}>{slots.workspace('nav', false)}</Box>
      </Box>
    ),
    bottomArea: (
      // 🔑 `mt: 'auto'` — 메뉴가 짧아도 발치는 **화면 맨 아래**에 붙는다(세로 flex 의 남는 자리를 먹는다)
      <Box sx={{ width: '100%', mt: 'auto' }}>
        {demoBannerMessage ? (
          <Box
            sx={{
              mx: 2,
              mb: 1,
              px: 1.5,
              py: 1,
              borderRadius: RADIUS.sm,
              bgcolor: 'rgba(0,0,0,0.8)',
              color: 'common.white',
            }}
          >
            <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.45 }}>
              {demoBannerMessage}
            </Typography>
          </Box>
        ) : null}
        {slots.navFooter}
      </Box>
    ),
  };

  /*
    🚨 **줄 앞 그림을 걷는다** — 이름만으로 충분히 갈리는 목록이라 그림이 칸만 먹는다.
      걷어 낸 만큼 메뉴를 좁혀(`--layout-nav-vertical-width`) 화면을 컨텐츠에 넘긴다.
  */
  const navSx = { [`& .${navSectionClasses.item.icon}`]: { display: 'none' } };

  const renderHeader = () => {
    const headerSlotProps = {
      container: {
        maxWidth: false,
        sx: {
          ...(isNavVertical && { px: { [layoutQuery]: 5 } }),
          ...(isNavHorizontal && {
            bgcolor: 'var(--layout-nav-bg)',
            height: { [layoutQuery]: 'var(--layout-nav-horizontal-height)' },
            [`& .${iconButtonClasses.root}`]: { color: 'var(--layout-nav-text-secondary-color)' },
          }),
        },
      },
    };

    const topBannerBoxSx = {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'center',
      minHeight: { xs: 34, sm: 34 },
    };

    const headerSlots = {
      topArea: demoBannerMessage ? (
        <Box
          sx={{
            ...topBannerBoxSx,
            backgroundColor: 'rgba(0,0,0,0.8)', color: 'white'
          }}
        >
          {prefersReducedMotion ? (
            <Typography variant="subtitle2">
              {demoBannerMessage}
            </Typography>
          ) : (
            <DemoBannerMarquee
              message={demoBannerMessage}
              durationSec={DEMO_BANNER_MARQUEE_DURATION_SEC}
            />
          )}
        </Box>
      ) : null,
      bottomArea: isNavHorizontal ? (
        <NavHorizontal
          data={navData}
          layoutQuery={layoutQuery}
          cssVars={navVars.section}
          checkPermissions={canDisplayItemByRole}
        />
      ) : null,
      leftArea: (
        <>
          {/** @slot Nav mobile */}
          {/* ⚠️ 햄버거는 **좁은 화면에서만** — 넓은 화면에는 왼쪽 메뉴가 이미 서 있다 */}
          <MenuButton
            onClick={onOpen}
            sx={{ mr: 1, ml: 1, [theme.breakpoints.up(layoutQuery)]: { display: 'none' } }}
          />
          <NavMobile
            data={navData}
            open={open}
            onClose={onClose}
            cssVars={navVars.section}
            checkPermissions={canDisplayItemByRole}
            slots={navSlots}
            sx={navSx}
          />

          {/** @slot Logo */}
          {/* {isNavHorizontal && (
            <Logo
              sx={{
                display: 'none',
                [theme.breakpoints.up(layoutQuery)]: { display: 'inline-flex' },
              }}
            />
          )} */}

          {/** @slot Divider */}
          {isNavHorizontal && (
            <VerticalDivider sx={{ [theme.breakpoints.up(layoutQuery)]: { display: 'flex' } }} />
          )}

          {/** @slot Workspace popover */}
          {/* {workspaces && workspaces.length > 0 && ( */}

          <Box sx={{ display: 'flex', alignItems: 'center' }}>{slots.workspace('header', isNavHorizontal)}</Box>
          {/* )} */}
        </>
      ),
      rightArea: (
        <Box sx={{ mr: 2, display: 'flex', alignItems: 'center', gap: { xs: 0, sm: 0.75 } }}>
          {/** @slot Searchbar */}
          {/* <Searchbar data={navData} /> */}

          {/** @slot Language popover */}
          {/* <LanguagePopover data={allLangs} /> */}

          {/** @slot Contacts popover */}
          {/* <ContactsPopover data={_contacts} /> */}

          {/** @slot Settings button */}
          {/* <SettingsButton /> */}

          {/** @slot Account drawer */}
          {slots.account}
        </Box>
      ),
    };

    return (
      <HeaderSection
        layoutQuery={layoutQuery}
        disableElevation={isNavVertical}
        {...slotProps?.header}
        slots={{ ...headerSlots, ...slotProps?.header?.slots }}
        slotProps={merge(headerSlotProps, slotProps?.header?.slotProps ?? {})}
        sx={[
          // 🚨 넓은 화면에서는 머리띠를 감춘다 — 그 자리의 것이 왼쪽 메뉴로 옮겨 갔다.
          //   가로 메뉴 모드는 머리띠가 메뉴를 쥐고 있어 그대로 둔다.
          ...(isNavHorizontal
            ? []
            : [{ [theme.breakpoints.up(layoutQuery)]: { display: 'none' } }]),
          ...(Array.isArray(slotProps?.header?.sx)
            ? slotProps.header.sx
            : [slotProps?.header?.sx]),
        ]}
      />
    );
  };

  /**
   * 왼쪽에 고정으로 서는 메뉴 — **넓은 화면에서만** 선다.
   * 🔑 `NavRoot` 가 `layoutQuery` 미만에서 `display: none` 이고, 그 아래로는 머리의 햄버거가 여는
   *   서랍(`NavMobile`)이 **같은 목록·같은 머리·같은 발치**를 편다(`navSlots`).
   *   콘텐츠는 `.sidebarContainer` 의 `pl` 로 그만큼 비켜선다.
   */
  const renderSidebar = () => (
    <NavVertical
      data={navData}
      isNavMini={isNavMini}
      layoutQuery={layoutQuery}
      cssVars={navVars.section}
      checkPermissions={canDisplayItemByRole}
      sx={navSx}
      slots={isNavMini ? undefined : navSlots}
    />
  );

  const renderFooter = () => null;

  /* 🔑 화면 머리(구역·메뉴 이름)는 **사이드바가 그리는 목록**에서 고른다 — 이름이 바뀌면 머리도 같이 바뀐다 */
  const pageHeading = resolveNavHeading(navData as unknown[], pathname, ACCOUNT_HEADING_EXTRAS, FULL_NAV_DATA);

  const renderMain = () => (
    <MainSection {...slotProps?.main}>
      <PageHeadingProvider value={pageHeading}>{children}</PageHeadingProvider>
    </MainSection>
  );

  return (
    <LayoutSection
      /** **************************************
       * @Header
       *************************************** */
      headerSection={renderHeader()}
      /** **************************************
       * @Sidebar
       *************************************** */
      sidebarSection={isNavHorizontal ? null : renderSidebar()}
      /** **************************************
       * @Footer
       *************************************** */
      footerSection={renderFooter()}
      /** **************************************
       * @Styles
       *************************************** */
      cssVars={{ ...dashboardLayoutVars(theme), ...navVars.layout, ...cssVars }}
      sx={[
        {
          /*
            🔑 메인 영역의 바탕은 흰색이다 — 회색(`background.default`)은 왼쪽 메뉴(`--layout-nav-bg`)만 쓴다.
            ⚠️ 흰 바탕 위의 카드는 그림자가 아니라 `divider` 테두리 한 줄로 갈린다(`MuiCard` · `HubSection`).
          */
          bgcolor: 'background.paper',
          [`& .${layoutClasses.sidebarContainer}`]: {
            [theme.breakpoints.up(layoutQuery)]: {
              pl: isNavMini ? 'var(--layout-nav-mini-width)' : 'var(--layout-nav-vertical-width)',
              transition: theme.transitions.create(['padding-left'], {
                easing: 'var(--layout-transition-easing)',
                duration: 'var(--layout-transition-duration)',
              }),
            },
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {analytics && <AnalyticsProvider />}
      {renderMain()}
    </LayoutSection>
  );
}
